import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { SurveyCard } from '@/surveys/states/surveys/survey-card';
import { Component, OnDestroy, OnInit, signal, computed, effect, inject, DestroyRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EcorrBanner } from './ecorr-banner';
import { EcorrBannerService } from './ecorr-banner.service';
import { EcorrVisibleBanner } from './ecorr-visible-banner';
import { PortalUser } from './../ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-ecorr-banner',
    templateUrl: './ecorr-banner.component.html',
    standalone: true,
    imports: [CommonModule]
})
export class EcorrBannerComponent implements OnDestroy {
    private destroyRef = inject(DestroyRef);
    private store = inject(Store<PortalUserState>);
    private ecorrBannerService = inject(EcorrBannerService);
    private router = inject(Router);

    // Signals for reactive state management
    error = signal('');
    route = signal('');
    previousRoute = signal('');
    activeSurveyCards = signal<SurveyCard[]>([]);
    activeBanners = signal<EcorrBanner[]>([]);
    loading = signal(false);
    bannerSet = signal<Set<EcorrVisibleBanner>>(new Set());
    visibleBanners = signal<EcorrVisibleBanner[]>([]);

    // Computed properties
    hasBanners = computed(() => this.bannerSet().size > 0);
    filteredBanners = computed(() => 
        [...this.bannerSet()].filter(banner => banner.show).sort((a, z) => 
            a.type.localeCompare(z.type, 'en', { sensitivity: 'base' })
        )
    );

    // Static configuration objects
    readonly icons = {
        info: 'fa-exclamation-circle',
        warning: 'fa-exclamation-triangle',
        error: 'fa-ban'
    } as const;

    readonly colors = {
        info: '#F1F3F5',
        warning: '#FFFFEE',
        error: '#FFDDDD'
    } as const;

    constructor() {
        this.initializeComponent();
    }

    private initializeComponent() {
        this.getPortalUser();
        this.loadBanners();
        
        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(e => {
                if (e instanceof NavigationEnd) {
                    this.route.set(e.url);
                    this.resetVisibility();
                }
            });
    }

    private getPortalUser() {
        this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(userObserver => {
                if (userObserver && userObserver.userAccountId > 0) {
                    this.activeSurveyCards.set(userObserver.activeSurveyCards);
                    this.resetVisibility();
                }
            });
    }

    private loadBanners() {
        if (this.loading()) {
            return;
        }
        
        this.loading.set(true);
        
        this.ecorrBannerService.getActiveBanners('ecorr')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data: EcorrBanner[]) => {
                    this.activeBanners.set(data);
                    this.loading.set(false);
                    this.resetVisibility();
                },
                error: error => {
                    this.loading.set(false);
                    this.error.set(error);
                }
            });
    }

    private resetVisibility() {
        const activeBanners = this.activeBanners();

        if (!activeBanners?.length) {
            return;
        }
        
        this.bannerSet.set(new Set<EcorrVisibleBanner>());
        
        const newBannerSet = new Set<EcorrVisibleBanner>();
        
        activeBanners.forEach(item => {
            const visibleBanner: EcorrVisibleBanner = new EcorrVisibleBanner();
            visibleBanner.trustedContent = item.messageContent;
            visibleBanner.icon = this.icons[item.bannerType.type];
            visibleBanner.displayLocation = item.displayLocation;
            visibleBanner.color = this.colors[item.bannerType.type];
            visibleBanner.show = this.determineVisibilty(item);
            visibleBanner.closable = item.closeable;
            visibleBanner.type = item.bannerType.type;
            visibleBanner.id = item.webappBannerId;

            this.addToSet(newBannerSet, visibleBanner);
        });
        
        this.bannerSet.set(newBannerSet);
    }

    private isInSet(set: Set<EcorrVisibleBanner>, banner: EcorrVisibleBanner): boolean {
        return Array.from(set).some(item => item.id === banner.id);
    }

    private addToSet(set: Set<EcorrVisibleBanner>, banner: EcorrVisibleBanner): void {
        if (!this.isInSet(set, banner)) {
            set.add(banner);
        }
    }

    private deleteFromSet(set: Set<EcorrVisibleBanner>, banner: EcorrVisibleBanner): void {
        const itemToDelete = Array.from(set).find(item => item.id === banner.id);
        if (itemToDelete) {
            set.delete(itemToDelete);
        }
    }

    private determineVisibilty(banner: EcorrBanner): boolean {
        const currentRoute = this.route();
        
        if (!currentRoute) {
            return false;
        }

        if (currentRoute.includes('faq')) {
            return false;
        }

        const path = banner.displayLocation?.toLowerCase();

        /* survey card based visibility */ 
        let display = false;
        const surveyCards = this.activeSurveyCards();
        
        if (banner.surveyCategory && surveyCards?.length) {
            surveyCards.forEach(survey => {
                if (path === 'all' || path === 'internal') {
                    if (banner.surveyCategory === survey.surveyCategory) {
                        if (banner.refPeriod) {
                            if (banner.refPeriod === survey.refPeriod) {
                                display = true;
                                return true;
                            } else {
                                return false;
                            }
                        } else {
                            display = true;
                            return true;
                        }
                    }
                }
            });
        } else if (!banner.surveyCategory) {
            if (currentRoute === '/' || currentRoute.includes('portal') || currentRoute.includes('login') || currentRoute.includes('register')) {
                return path === 'all' || path === 'external';
            } else if (currentRoute.includes('surveys') || currentRoute.includes('case-list') || currentRoute.includes('submit-case') ||
                    currentRoute.includes('case-detail') || currentRoute.includes('add-comment')) {
                return path === 'all' || path === 'internal';
            } else {
                return path === 'all' || path === 'external';
            }
        }
        return display;
    }

    hideBanner(visibleBanner: EcorrVisibleBanner): void {
        const currentSet = this.bannerSet();
        this.deleteFromSet(currentSet, visibleBanner);
        this.bannerSet.set(new Set(currentSet));
    }

    ngOnDestroy() {
        // No need for manual subscription cleanup with takeUntilDestroyed
        // The destroyRef will automatically handle cleanup
    }
}